import { useLDSafeFlags } from 'hooks/useLDSafeFlags'
import React, { FC } from 'react'

interface LaunchDarklyDependentContentProps {
  flagName: string
  /**
   * children are rendered when launch darkly has been initialized or failed loading
   */
  children: React.ReactChild
  /**
   * loadingContent is rendered while launch darkly is in the loading state : defaults to a blank react fragment
   */
  loadingContent?: React.ReactChild
  /**
   * Content to display if the flag is false or not found
   */
  fallbackContent: React.ReactChild | null
}

/**
 * This component is intended to be used when content depends on launch darkly being loaded
 * With this component we can wait to render display data until after launch darkly has loaded
 * or failed. In addition, if launch darkly fails to load (meaning flags default to false) we can
 * render other content, for instance a redirect to a 404 page.
 * @param props
 * @returns a JSX react fragment containing content based on whether launch darkly is loaded
 */
export const LaunchDarklyDependentContent: FC<LaunchDarklyDependentContentProps> = ({
  flagName,
  children,
  loadingContent,
  fallbackContent
}) => {
  const flags = useLDSafeFlags()

  if (flags === 'error') {
    return <>{fallbackContent}</>
  } else if (flags && flags[flagName] !== undefined) {
    return flags[flagName] ? <>{children}</> : <>{fallbackContent}</>
  } else {
    return loadingContent ? <>{loadingContent}</> : <></>
  }
}
